import { useTranslation } from 'react-i18next';
import { Button, Select, NumberInput, Switch, TextInput, Textarea, Badge } from '@mantine/core';
import { IconSettings, IconBuildingBank, IconDeviceFloppy, IconCheck, IconRefresh, IconPercentage, IconTag } from '@tabler/icons-react';
import { Business, TenantBusiness, DiscountCategory } from '@turizi/shared';
import { useState, useEffect } from 'react';
import { useUpdateTenantBusiness, useDiscountCategories } from '@/hooks/api-hooks';
import CurrencySelector from './currency-selector';

interface Props {
    business: Business;
}

export default function BusinessCommercialTab({ business }: Props): JSX.Element {
    const { t } = useTranslation();
    const updateTenantBusinessMutation = useUpdateTenantBusiness();
    const {
        data: discountCategories,
    } = useDiscountCategories();
    const discountCategoriesData = discountCategories?.data || [];

    // State for Tenant Business editing (mandatory relation)
    const [tbDraft, setTbDraft] = useState<TenantBusiness | undefined>(business.tenant_business);
    const [tbBackup, setTbBackup] = useState<TenantBusiness | undefined>(business.tenant_business);
    const [saving, setSaving] = useState(false);

    // Initialize state when data loads
    useEffect(() => {
        if (business.tenant_business) {
            setTbDraft(business.tenant_business);
            setTbBackup(business.tenant_business);
        }
    }, [business]);

    const saveTb = (draftTb: TenantBusiness) => {
        setSaving(true);
        updateTenantBusinessMutation.mutate(draftTb, {
            onSuccess: (data) => {
                if (data?.data) {
                    setTbDraft(data.data);
                    setTbBackup(data.data);
                }
                setSaving(false);
            },
            onError: () => {
                setSaving(false);
            }
        });
    };

    const resetChanges = () => {
        if (tbBackup) {
            setTbDraft({ ...tbBackup });
        }
    };

    const hasTbInfoChanges = () => {
        if (!tbDraft || !tbBackup) return false;
        const draftMult = tbDraft.cost_multiplier != null ? Number(tbDraft.cost_multiplier) : null;
        const backupMult = tbBackup.cost_multiplier != null ? Number(tbBackup.cost_multiplier) : null;
        return tbDraft.discount_category !== tbBackup.discount_category ||
            tbDraft.default_currency !== tbBackup.default_currency ||
            draftMult !== backupMult ||
            tbDraft.rank !== tbBackup.rank ||
            tbDraft.publish_prices !== tbBackup.publish_prices ||
            tbDraft.allow_online_reservations !== tbBackup.allow_online_reservations ||
            tbDraft.is_favorite !== tbBackup.is_favorite ||
            (tbDraft.bank_name || '') !== (tbBackup.bank_name || '') ||
            (tbDraft.bank_account_holder || '') !== (tbBackup.bank_account_holder || '') ||
            (tbDraft.bank_account_number || '') !== (tbBackup.bank_account_number || '') ||
            (tbDraft.bank_account_type || '') !== (tbBackup.bank_account_type || '') ||
            (tbDraft.bank_cbu_iban || '') !== (tbBackup.bank_cbu_iban || '') ||
            (tbDraft.bank_alias || '') !== (tbBackup.bank_alias || '') ||
            (tbDraft.bank_tax_id || '') !== (tbBackup.bank_tax_id || '') ||
            (tbDraft.bank_swift_code || '') !== (tbBackup.bank_swift_code || '') ||
            (tbDraft.bank_notes || '') !== (tbBackup.bank_notes || '');
    };

    if (!tbDraft) {
        return (
            <div className="solid-box-border max-w-xl text-center py-6 text-sm text-gray-500">
                {t('loading')}...
            </div>
        );
    }

    const hasChanges = hasTbInfoChanges();
    const activeCategory = tbDraft.discount_category;

    return (
        <div className="space-y-6 max-w-4xl pb-16">

            {/* Section 1: Commercial & Pricing Configuration */}
            <div className="solid-box-border space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <IconSettings size={18} className="text-gray-500" />
                        <h3 className="text-sm font-bold text-gray-800">{t('commercial_settings')}</h3>
                    </div>
                </div>

                {/* Primary Row: Discount Category & Currency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Select
                            label={t('discount_category')}
                            placeholder={t('select')}
                            className="font-semibold"
                            value={tbDraft.discount_category}
                            data={discountCategoriesData.map((v: DiscountCategory) => v.category)}
                            onChange={(value) => value && setTbDraft(prev => prev ? { ...prev, discount_category: value } : undefined)}
                            searchable
                            clearable
                        />
                    </div>

                    <CurrencySelector
                        label={t('default_currency')}
                        value={tbDraft.default_currency}
                        onChange={currency => setTbDraft(prev => prev ? {
                            ...prev,
                            default_currency: currency,
                        } : undefined)}
                    />

                    <NumberInput
                        hideControls
                        placeholder='Ejemplo: 0.80 (20% descuento)'
                        className='font-semibold'
                        label={t('cost_multiplier')}
                        description={t('cost_multiplier_desc')}
                        value={tbDraft.cost_multiplier != null ? Number(tbDraft.cost_multiplier) : ''}
                        onChange={(val) => setTbDraft({ ...tbDraft, cost_multiplier: val === '' || val == null ? null : Number(val) })}
                    />
                </div>

                {/* Toggles and Visibility Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200/80 bg-gray-50/50">
                        <div className="flex flex-col">
                            <span className="text-xs font-semibold text-gray-800">{t('allow_online_reservations')}</span>
                        </div>
                        <Switch
                            checked={tbDraft.allow_online_reservations || false}
                            onChange={(e) => setTbDraft(prev => prev ? { ...prev, allow_online_reservations: e.target.checked } : undefined)}
                            size="sm"
                            color="black"
                        />
                    </div>

                    <div className="p-3 rounded-lg border border-gray-200/80 bg-gray-50/50 flex items-center gap-2">
                        <NumberInput
                            label={t('public_rank')}
                            className="font-semibold w-full"
                            placeholder="--"
                            value={tbDraft.rank ?? ''}
                            onChange={(value) => setTbDraft(prev => prev ? { ...prev, rank: value === '' ? null : Number(value) } : undefined)}
                            min={0}
                            hideControls
                        />
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200/80 bg-gray-50/50">
                        <div className="flex flex-col">
                            <span className="text-xs font-semibold text-gray-800">{t('is_favorite')}</span>
                        </div>
                        <Switch
                            checked={tbDraft.is_favorite || false}
                            onChange={(e) => setTbDraft(prev => prev ? { ...prev, is_favorite: e.target.checked } : undefined)}
                            size="sm"
                            color="black"
                        />
                    </div>
                </div>
            </div>

            {/* Section 3: Banking Data */}
            <div className="solid-box-border space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                    <IconBuildingBank size={18} className="text-gray-500" />
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">{t('banking_data')}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <TextInput
                        label={t('bank_name')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_name || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_name: e.target.value } : undefined)}
                    />
                    <TextInput
                        label={t('bank_account_holder')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_account_holder || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_account_holder: e.target.value } : undefined)}
                    />
                    <TextInput
                        label={t('bank_tax_id')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_tax_id || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_tax_id: e.target.value } : undefined)}
                    />
                    <Select
                        label={t('bank_account_type')}
                        className="font-semibold text-xs"
                        placeholder="--"
                        value={tbDraft.bank_account_type || null}
                        data={[
                            { value: 'checking', label: t('bank_account_type_checking') },
                            { value: 'savings', label: t('bank_account_type_savings') },
                        ]}
                        onChange={(value) => setTbDraft(prev => prev ? { ...prev, bank_account_type: value || null } : undefined)}
                        clearable
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <TextInput
                        label={t('bank_account_number')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_account_number || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_account_number: e.target.value } : undefined)}
                    />
                    <TextInput
                        label={t('bank_alias')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_alias || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_alias: e.target.value } : undefined)}
                    />
                    <TextInput
                        label={t('bank_cbu_iban')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_cbu_iban || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_cbu_iban: e.target.value } : undefined)}
                    />
                    <TextInput
                        label={t('bank_swift_code')}
                        className="font-semibold text-xs"
                        value={tbDraft.bank_swift_code || ''}
                        onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_swift_code: e.target.value } : undefined)}
                    />
                </div>

                <Textarea
                    label={t('bank_notes')}
                    className="font-semibold text-xs"
                    rows={2}
                    autosize
                    minRows={2}
                    value={tbDraft.bank_notes || ''}
                    onChange={(e) => setTbDraft(prev => prev ? { ...prev, bank_notes: e.target.value } : undefined)}
                />
            </div>

            {/* Bottom Floating Bar when changes exist */}
            {hasChanges && (
                <div className="sticky bottom-4 z-20 flex items-center justify-between p-3 px-4 rounded-xl bg-slate-900 text-white shadow-xl border border-slate-800">
                    <div className="flex items-center gap-2 text-xs font-medium">
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        <span>{t('unsaved_changes')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            onClick={resetChanges}
                            disabled={saving}
                            className="text-gray-300 hover:text-white hover:bg-slate-800"
                        >
                            {t('discard')}
                        </Button>
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            leftSection={<IconDeviceFloppy size={14} />}
                            onClick={() => saveTb(tbDraft)}
                            loading={saving}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

